import { Map } from "immutable";
import { uniqueId, cloneDeep } from "lodash";

import { Record as ImmutableRecord } from "immutable";
import { ImmutableMap } from "src/helpers/ImmutableHelpers";
import {
  hasEmptyKeys,
  changeNestedNullToEmptyString,
} from "src/helpers/common";

import {
  IServiceUser,
  IEditServiceFormDetails,
  IUpsertServiceFormState,
  RecordType,
  newUserObject,
  UpsertServiceFormState,
  newDetailObject,
} from "src/reducers/UpsertServiceForm";
import {
  SelectTypeEnum,
  SelectVersionEnum,
} from "src/components/Lawyer/Services/UpsertServiceForm/DynamicServiceForm/ServiceStepRow";
import { RightNavBarOption } from "src/actions/RightNavBar";
import { getReferences } from "src/components/Lawyer/Services/UpsertServiceForm/Common/serviceFormObject";
import {
  ServiceItemMode,
  ServiceItemTypeIdByString,
  ServiceItemTypeKindIdByString,
} from "src/types/types";

const moment = require("moment");

const ALERT_MODE = ServiceItemMode.Alert;
const ALERT_TYPE_ID = ServiceItemTypeIdByString["Alert"];
const ALERT_KIND_ID =
  (typeof ServiceItemTypeKindIdByString !== "undefined"
    ? ServiceItemTypeKindIdByString["Alert"]
    : null) || 11001;

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const extractFieldsFromTemplate = (
  template: string,
  description: string
): { [key: string]: string } | null => {
  if (!template || !description) return null;

  const keys: Array<"field1" | "field2" | "field3" | "field4"> = [];
  const tokenRegex = /\[field([1-4])\]/g;
  let m: RegExpExecArray | null = null;
  while ((m = tokenRegex.exec(template)) !== null) {
    const idx = Number(m[1]);
    if (idx >= 1 && idx <= 4) {
      keys.push(`field${idx}` as const);
    }
  }
  if (!keys.length) return null;

  const pattern = escapeRegExp(template).replace(
    /\\\[field[1-4]\\\]/g,
    "(.+?)"
  );
  const re = new RegExp(`^${pattern}$`, "i");
  const match = re.exec(description.trim());
  if (!match) return null;

  const out: { [key: string]: string } = {};
  keys.forEach((k, i) => {
    out[k] = (match[i + 1] || "").trim();
  });
  return out;
};

export const setServiceFormData = (
  state: ImmutableRecord<IUpsertServiceFormState>,
  serviceData: ImmutableMap<IEditServiceFormDetails>,
  allKinds: { [key: string]: any }
) => {
  const kindsMap: { [typeId: number]: Array<any> } =
    allKinds.registerItemTypeKind ?? allKinds;

  serviceData = changeNestedNullToEmptyString(serviceData);

  return state.withMutations((mutable) => {
    let steps = serviceData.get("stages");
    let notes = serviceData.get("notes");

    steps = steps.map((step: ImmutableMap<any>) => {
      const rawTypeName = (step.get("type") || "").trim();
      const rawKind = (step.get("kind") || "").replace(/\s+$/, "");

      const incomingMode = step.get("registerItemModeId");

      const looksLikeAlert =
        rawTypeName === "Alert" ||
        rawKind === "Alert" ||
        incomingMode === ServiceItemMode.Alert;

      let registerItemTypeId = looksLikeAlert
        ? ALERT_TYPE_ID
        : ServiceItemTypeIdByString[rawTypeName];

      const kindsForType =
        (allKinds.registerItemTypeKind ?? allKinds)[registerItemTypeId] || [];

      // bazowe wartości
      let finalKindId = step.get("registerItemTypeKindId");
      let finalKind = step.get("kind");
      let grants = step.get("grants");

      let extractedFields: { [key: string]: string } | null = null;

      if (looksLikeAlert) {
        // ALERT - zawsze stały kindId + nazwa + grants z definicji kinda
        finalKindId = ALERT_KIND_ID;
        finalKind = "Alert";
        const alertKindDef = kindsForType.find(
          (k: any) => k.id === ALERT_KIND_ID
        );
        if (alertKindDef?.grants) {
          grants = alertKindDef.grants;
        }
      } else {
        // priorytet: match po kindId z backendu (rozróżnia kindy o tej samej nazwie, np. 2101 vs 2103)
        // fallback: match po nazwie kinda
        const initialKindId = step.get("registerItemTypeKindId");
        let match: any = null;
        if (initialKindId) {
          match = kindsForType.find((k: any) => k.id === initialKindId);
        }
        if (!match) {
          match = kindsForType.find((k: any) => k.name === rawKind);
        }
        if (match) {
          finalKindId = match.id;
          finalKind = match.name;
          grants = match.grants;
        } else if (!finalKindId) {
          const desc = (step.get("description") || "").trim();
          if (desc) {
            let tmplMatch: any = null;
            for (const k of kindsForType) {
              if (!k?.descTemplate) continue;
              const extracted = extractFieldsFromTemplate(k.descTemplate, desc);
              if (extracted) {
                tmplMatch = k;
                extractedFields = extracted;
                break;
              }
            }
            if (!tmplMatch) {
              const allKinds = Object.values(kindsMap).flatMap((v: any) =>
                Array.isArray(v) ? v : []
              );
              for (const k of allKinds) {
                if (!k?.descTemplate) continue;
                const extracted = extractFieldsFromTemplate(k.descTemplate, desc);
                if (extracted) {
                  tmplMatch = k;
                  extractedFields = extracted;
                  break;
                }
              }
            }
            if (tmplMatch) {
              finalKindId = tmplMatch.id;
              finalKind = tmplMatch.name;
              grants = tmplMatch.grants;
              if (tmplMatch.typeId) {
                registerItemTypeId = tmplMatch.typeId;
              }
            }
          }
        }
      }

      // Tryb: dla Alertu wymuszamy 3, w innym wypadku honorujemy incomingMode, domyślnie Etap
      const mode = looksLikeAlert
        ? ALERT_MODE
        : incomingMode != null
        ? incomingMode
        : rawTypeName === "Alert"
        ? ALERT_MODE
        : ServiceItemMode.Etap;

      let result = step
        .set("created_at", moment(step.get("created_at")).toDate())
        .set("registerItemModeId", mode)
        .set("registerItemTypeId", registerItemTypeId)
        .set("registerItemTypeKindId", finalKindId)
        .set("kind", finalKind)
        .set("grants", grants);

      if (extractedFields) {
        (["field1", "field2", "field3", "field4"] as const).forEach((key) => {
          const current = result.get(key);
          if (!current && extractedFields[key]) {
            result = result.set(key, extractedFields[key]);
          }
        });
      }

      return result;
    });

    notes = notes.map((note: ImmutableMap<any>) => {
      const rawTypeName = (note.get("type") || "").trim();
      const registerItemTypeId = ServiceItemTypeIdByString[rawTypeName];

      // tylko usuwamy spacje z końca
      const rawKind = (note.get("kind") || "").replace(/\s+$/, "");

      const kindsForType = kindsMap[registerItemTypeId] || [];

      let finalKindId = note.get("registerItemTypeKindId");
      let finalKind = note.get("kind");
      let grants = note.get("grants");

      // priorytet: match po kindId z backendu; fallback: match po nazwie
      let match: any = null;
      if (finalKindId) {
        match = kindsForType.find((k: any) => k.id === finalKindId);
      }
      if (!match) {
        match = kindsForType.find((k: any) => k.name === rawKind);
      }

      if (match) {
        finalKindId = match.id;
        finalKind = match.name;
        grants = match.grants;
      }

      return note
        .set("created_at", moment(note.get("created_at")).toDate())
        .set("registerItemModeId", ServiceItemMode.Notatka)
        .set("registerItemTypeId", registerItemTypeId)
        .set("registerItemTypeKindId", finalKindId)
        .set("kind", finalKind)
        .set("grants", grants);
    });

    steps = steps
      .merge(notes)
      .sort((a: any, b: any) => (a.get("date") > b.get("date") ? 1 : -1));

    const details = serviceData.get("details") || newDetailObject;
    const subject = filterValuesFromPeopleList(details, 0)?.get(0) || null;
    const description: ImmutableMap<any> = Map({
      description: details.get("description"),
      domain_id: serviceData.getIn(["domain", "id"]),
      domain_type_id: serviceData.getIn(["domainType", "id"]),
      loanAnalysis: details.get("loanAnalysis"),
      settlementAnalysis: details.get("settlementAnalysis"),
      aiLoanExtraction: details.get("aiLoanExtraction"),
      needsReanalysis: serviceData.get("needsReanalysis"),
    });
    const opponents = filterValuesFromPeopleList(details, 1);
    const offices = filterValuesFromPeopleList(details, 2);
    const witnesses = filterValuesFromPeopleList(details, 3);

    mutable
      .set("steps", steps)
      .set("subject", subject || newUserObject(uniqueId(), 0))
      .set("details", details)
      .set("description", description)
      .set(
        "opponents",
        opponents.map((el: any) => el.set("key", uniqueId()))
      )
      .set(
        "offices",
        offices.map((el: any) => el.set("key", uniqueId()))
      )
      .setIn(["consequence", "consequences"], details.get("stateAfter"))
      .setIn(["condition", "before_state"], details.get("stateBefore"))
      .set(
        "witnesses",
        witnesses.map((el: any) => el.set("key", uniqueId()))
      )
      .setIn(
        ["additionalInformation", "additional_information"],
        details.get("extraInformation")
      )
      .set("guardianId", details.get("guardian_id"))
      .set("serviceId", serviceData.get("id"));

    mutable
      .setIn(["data_saved", "steps"], !!steps && steps.size > 0)
      .setIn(["data_saved", "subject"], !!subject && subject.size > 0)
      .setIn(["data_saved", "description"], !hasEmptyKeys(description))
      .setIn(["data_saved", "opponents"], !!opponents && opponents.size > 0)
      .setIn(["data_saved", "offices"], !!offices && offices.size > 0)
      .setIn(["data_saved", "consequence"], !!details.get("stateAfter"))
      .setIn(["data_saved", "condition"], !!details.get("stateBefore"))
      .setIn(["data_saved", "witnesses"], !!witnesses && witnesses.size > 0)
      .setIn(
        ["data_saved", "additionalInformation"],
        !!details.get("extraInformation")
      )
      .setIn(["data_saved", "guardian"], !!serviceData.get("guardian_id"));
  });
};

const filterValuesFromPeopleList = (
  data: ImmutableMap<IEditServiceFormDetails>,
  recordType: RecordType
) => {
  const cloned = cloneDeep(data);

  return cloned
    .get("cards")
    .filter((el: ImmutableMap<IServiceUser>) => el.get("side") === recordType);
};
const handleContacts = (
  state: UpsertServiceFormState,
  path: string[],
  value: string | number,
  name: string,
  firstInPath: string
) => {
  const [parent, child, i, contacts] = path;
  const hasExistingParent = state.getIn([firstInPath, parent, child, i]);
  if (hasExistingParent) {
    return state.setIn(
      [firstInPath, parent, child, i, contacts],
      [{ [name]: value }]
    );
  } else {
    const existingPeople = state.getIn([firstInPath, parent, child]);
    if (existingPeople) {
      return state.setIn(
        [firstInPath, parent, child],
        [...existingPeople, { contacts: [{ [name]: value }] }]
      );
    } else {
      return state.setIn(
        [firstInPath, parent, child],
        [{ contacts: [{ [name]: value }] }]
      );
    }
  }
};

const handleCurrent = (
  current: any,
  index: number,
  name: string,
  value: string | number
) => {
  if (current[index]) {
    return current.map((el: any, i: number) =>
      i === index ? { ...current[index], [name]: value } : el
    );
  } else {
    return [...current, { [name]: value }];
  }
};

export const handleArrayData = (
  state: UpsertServiceFormState,
  { index, name, value, path }: any,
  firstInPath: string
) => {
  const current = state.getIn([firstInPath, ...path]);
  if (current) {
    return state.setIn(
      [firstInPath, ...path],
      handleCurrent(current, index, name, value)
    );
  } else {
    return path.length === 4
      ? handleContacts(state, path, value, name, firstInPath)
      : state.setIn([firstInPath, ...path], [{ [name]: value }]);
  }
};

export interface IStep {
  id: number;
  type: number;
  version: number;
  position: number;
  created_at: string;
  next_action_date: string | null;
  admin_id: number;
  description: string;
  service_id: number;
}

export const getSelectorsValues = (
  selectorValues: any,
  select: string,
  state?: UpsertServiceFormState,
  branchId?: number
) => {
  const { select1, select2, select3 } = selectorValues;
  let branch;
  if (branchId) {
    branch = branchId;
  }
  if (state) {
    branch = state.get("selectedBranch").get("value");
  }
  if (!branch || !select1 || !select2 || !select3) {
    return;
  }
  const object = getReferences();
  if (select === "select_1") {
    return object[select][branch][selectorValues.select1];
  } else if (select === "select_2") {
    return object[select][select1][select2];
  } else {
    return object[select][select1][select3];
  }
};

const tabsAsObject = [
  "subject",
  "relations",
  "before_state",
  "consequences",
  "additional_information_tab",
];

export const resultOutput = ({ steps, people, matter }: any) => {
  const newSteps = steps
    .map((step: IStep) => ({
      ...step,
      type: {
        value: step.type,
        label: SelectTypeEnum[step.type],
      },
      version: {
        value: step.version,
        label: SelectVersionEnum[step.version],
      },
    }))
    .sort((step1: IStep, step2: IStep) =>
      step1.position > step2.position ? 1 : -1
    );
  const newMatter = {
    ...matter,
    date: matter.date
      ? moment.utc(matter.date).local().format("DD/MM/YYYY")
      : null,
    hour: matter.date ? moment.utc(matter.date).local().format("HH:mm") : null,
  };
  const result = {
    actions: { row: newSteps },
    description: { matter: newMatter },
  };
  const assignPerson = (person: any) => {
    const { field_name, record_type } = person;
    if (typeof result[RightNavBarOption[record_type]] === "undefined") {
      result[RightNavBarOption[record_type]] = {};
    }
    if (
      typeof result[RightNavBarOption[record_type]][field_name] === "undefined"
    ) {
      result[RightNavBarOption[record_type]][field_name] = [
        ...tabsAsObject,
      ].includes(field_name)
        ? {}
        : [];
    }

    const keyToModify = Object.keys(person).filter((item) =>
      item.includes("date")
    );

    const newPerson = {
      ...person,
    };

    keyToModify.forEach((key: string) => {
      if (newPerson[key]) {
        const valid = moment(newPerson[key]).isValid();
        newPerson[key] = valid
          ? moment.utc(newPerson[key]).local().format("DD/MM/YYYY")
          : null;
      }
    });

    if (result[RightNavBarOption[record_type]][field_name] instanceof Array) {
      result[RightNavBarOption[record_type]][field_name].push(newPerson);
    } else {
      result[RightNavBarOption[record_type]][field_name] = newPerson;
    }
  };
  people.forEach(assignPerson);
  return result;
};
